<script setup lang="ts">
import { computed, ref, onMounted } from 'vue'
import type { Rating, Place, Client } from '~/types/types'
import Vue3StarRatings from '~/components/Vue3StarRatings.vue'
import { useAuthStore } from '@/store/auth'
/* ========================
   Props Definition
   ======================== */
const props = defineProps<{
  place_item: Place
  classShow?: string
  filterText?: string
}>()


/* ========================
   Repository, Store & Modal
   ======================== */
const authStore = useAuthStore()



const highlightMatch = (text: string): string => {
  const keyword = props.filterText?.trim();
  
  if (!keyword) return text;

  const escapedKeyword = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // escape RegEx
  const regex = new RegExp(`(${escapedKeyword})`, 'gi');

  return text.replace(regex, '<span class="highlight">$1</span>');
};

/* ========================
   Utility Functions
   ======================== */
const slugify = (text: string): string =>
text ? text.trim().replace(/\s+/g, '-').toLowerCase() : ''

// /* ========================
//    Local Reactive State (initialized on mount)
//    ======================== */

/* ========================
   Computed Properties
   ======================== */
// Wrap the incoming prop for easier use
const place = computed(() => props.place_item)

// Build the user profile link (adjust logic as needed)
// Build the place details link using slugified name
const placeLink = computed((): string => {
  return `/place/${place.value.tenant}`
})


 
// Image gallery computed properties with safe array checks
const imageCount = computed((): number =>
  place.value.images ? place.value.images.length : 0
)
const hiddenImageCount = computed((): number =>
  imageCount.value > 4 ? imageCount.value - 4 : 0
)
const hiddenImageText = computed((): string =>
  hiddenImageCount.value > 0 ? ` + ${hiddenImageCount.value}` : ' + '
)
const galleryClass = computed((): string => {
  switch (imageCount.value) {
    case 1:
      return 'one-image'
    case 2:
      return 'two-images'
    case 3:
      return 'three-images'
    default:
      return  ''
  }
})

// Split images into visible (first 4) and hidden arrays safely
const visibleImages = computed(() =>
  place.value.images ? place.value.images.slice(0, 4) : []
)
const hiddenImages = computed(() =>
  place.value.images ? place.value.images.slice(4) : []
)


const showChar = 245;
const showCharLg = 320;
const ellipsestext = "...";
const moretext = "عرض المزيد";
const lesstext = "";

const expanded = ref(false);

const hasMore = ref(false);


const truncatedText = computed(() => {

  let reviewText = ref('');

  if (place.value.description.length > showChar) {
    hasMore.value = true;
    reviewText.value = place.value?.description ? place.value?.description?.substring(0, limit) : '';
    
  } else {
    reviewText.value = place.value.description;
  }

  return reviewText.value;

});

const toggleExpand = () => {
  expanded.value = !expanded.value;
};


</script>

<template>

  <div class="col-md-12">
      <div class="item_rate">
          <div class="item_image">
              <NuxtLink :to="`/place/${place.tenant}`">

                  <NuxtImg
                      :src="place?.main_image"
                      :alt="place.name"
                      loading="lazy"
                      :custom="true"
                      v-slot="{ src, isLoaded, imgAttrs }"
                  >
                      <!-- Show the actual image when loaded -->
                      <img
                      v-if="isLoaded"
                      v-bind="imgAttrs"
                      :src="src"
                      >

                      <!-- Show a placeholder while loading -->
                      <img
                      v-else
                      :src="'/website/assets/img/default-featured-image.jpeg'"
                      >
                  </NuxtImg>
                  <!-- <img loading="lazy" :src="place?.main_image || '/uploads/avatar.png'"
                      :alt="place?.name || 'مكان غير معروف'" /> -->
              </NuxtLink>
          </div>
          <div class="item_content">
              <NuxtLink :to="`/place/${place.tenant}`">
                  <h3 v-html="highlightMatch(place?.name || 'اسم غير متوفر')"></h3>
              </NuxtLink>
              <div class="stars categories">
                  <Vue3StarRatings :modelValue="place.total_rate_db" :numberOfStars="5"
                      :starSize="18" :disableClick="true" />
                  <span class="rate">( {{ place.total_rate_db.toFixed(1) }} )</span>
              </div>
              <p class="pb-0">
                <span v-if="expanded" v-html="highlightMatch(place.description)"></span>
                <span v-else>
                  <span v-html="highlightMatch(truncatedText)">
                  </span>
                  <span class="moreelipses" v-if="hasMore">{{ ellipsestext }}</span>
                </span>
                  <a href="javascript:void(0)" @click.prevent="toggleExpand" class="toggle-button" v-if="hasMore">
                    {{ expanded ? lesstext : moretext }}
                  </a>
              </p>
              <div class="item_rate_body_attach categories">
            <!-- Image Gallery -->
            <div class="attached_images" :class="{ hidden: imageCount === 0 }">
              <div
                :class="[galleryClass, 'rate-gallery-' + place.id, 'rate-gallery']"
                :data-image-count="imageCount"
              >
                <template v-for="(image, index) in visibleImages" :key="index">
                  <a
                    :href="image.large"
                    :data-fancybox="`image${place.id}`"
                  >
                    <img
                      loading="lazy"
                      :src="image.medium"
                      class="image-slide-show"
                      :alt="place.name || '-'"
                    />
                  </a>
                </template>
         
              </div>
            </div>
            <div class="distance" v-if="place.distance && place.distance < 10000">
              <svg id="location_linear" data-name="location/linear" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                <g id="vuesax_linear_location" data-name="vuesax/linear/location">
                  <g id="location">
                    <path id="Vector" d="M6.24,3.12A3.12,3.12,0,1,1,3.12,0,3.12,3.12,0,0,1,6.24,3.12Z" transform="translate(8.88 7.19)" fill="none" stroke="#292d32" stroke-width="1.5"/>
                    <path id="Vector-2" data-name="Vector" d="M.243,6.49c1.97-8.66,14.8-8.65,16.76.01,1.15,5.08-2.01,9.38-4.78,12.04a5.193,5.193,0,0,1-7.21,0C2.253,15.88-.907,11.57.243,6.49Z" transform="translate(3.377 2)" fill="none" stroke="#292d32" stroke-width="1.5"/>
                    <path id="Vector-3" data-name="Vector" d="M0,0H24V24H0Z" transform="translate(24 24) rotate(180)" fill="none" opacity="0"/>
                  </g>
                </g>
              </svg>

              <span>
                  {{ place.distance < 1 ? `${Math.round(place.distance * 1000)} m` : `${place.distance.toFixed(1)} km` || 0 }}
              </span>
          </div>
          </div>
          </div>
          <div class="visitors">
              <img src="/website/assets/img/icons/circle_user.png" alt="user" />
              <span>
                  {{ place.rates_count_db || 0 }}
              </span>
          </div>
          
          
      </div>
  </div>

  
</template>
<style>

.animating {
  animation: pop 0.5s ease-in-out;
}
@keyframes pop {
  0% { transform: scale(1); }
  50% { transform: scale(1.2); }
  100% { transform: scale(1); }
}

</style>